import { motion } from "framer-motion";
import { Zap, ScanLine, Check } from "lucide-react";
import { ASSETS, DIFFERENCES } from "@/lib/content";
import { PhoneFrame } from "@/components/PhoneFrame";

const FeatureRow = ({ icon: Icon, eyebrow, title, body, points, src, alt, reverse, testId }) => (
  <div
    data-testid={testId}
    className={`grid grid-cols-1 items-center gap-12 lg:grid-cols-2 ${
      reverse ? "lg:[&>*:first-child]:order-2" : ""
    }`}
  >
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
    >
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
        <Icon size={24} />
      </span>
      <p className="mt-5 font-heading text-sm font-bold uppercase tracking-widest text-emerald-600">
        {eyebrow}
      </p>
      <h3 className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
        {title}
      </h3>
      <p className="mt-4 text-lg text-gray-600">{body}</p>
      <ul className="mt-6 space-y-3">
        {points.map((p) => (
          <li key={p} className="flex items-center gap-3 text-gray-700">
            <Check size={18} className="shrink-0 text-emerald-500" strokeWidth={3} />
            <span className="font-medium">{p}</span>
          </li>
        ))}
      </ul>
    </motion.div>

    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="flex justify-center"
    >
      <PhoneFrame src={src} alt={alt} />
    </motion.div>
  </div>
);

export const Features = () => {
  return (
    <section id="features" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="font-heading text-sm font-bold uppercase tracking-widest text-emerald-600">
            What makes Purrse different
          </p>
          <h2 className="mt-3 font-heading text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
            A money app that feels good to open.
          </h2>
        </motion.div>

        <div className="mt-20 space-y-28">
          <FeatureRow
            testId="feature-logging"
            icon={Zap}
            eyebrow="Quick logging"
            title="Log a spend in a single tap."
            body="Quick-add tiles for the stuff you actually buy. Tap, pick a bucket, done — no forms, no friction."
            points={["Coffee $5, Brunch $25, Dinner out $40", "Assign to your joy buckets instantly", "Optional note when you want one"]}
            src={ASSETS.shots.addExpense}
            alt="Add expense screen"
          />
          <FeatureRow
            testId="feature-scan"
            icon={ScanLine}
            eyebrow="Receipt scanning"
            title="Snap a receipt. Bast does the rest."
            body="Bast reads the total and suggests a bucket. You confirm before anything lands in your budget."
            points={["Reads totals automatically", "Suggests the right bucket", "You always confirm first"]}
            src={ASSETS.shots.scanReceipt}
            alt="Scan receipt screen"
            reverse
          />
        </div>

        <div className="mt-28 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {DIFFERENCES.map((d, i) => (
            <motion.div
              key={d.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`difference-card-${i}`}
              className="rounded-3xl border border-white/60 bg-white p-8 shadow-xl shadow-black/5 transition-transform hover:-translate-y-1"
            >
              <h3 className="font-heading text-xl font-bold text-gray-900">{d.title}</h3>
              <p className="mt-2 text-gray-600">{d.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
